import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, useToast, Flex, Switch,
} from '@chakra-ui/react';
import api from '../api';

const TYPE_LABELS = {
  daily: 'Daily', days: 'Every X Days', weekly: 'Weekly', monthly: 'Monthly',
  quarterly: 'Quarterly', biannual: 'Bi-Annual', yearly: 'Yearly',
};
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function RecurringTasksModal({ isOpen, onClose }) {
  const [tasks, setTasks] = useState([]);
  const toast = useToast();

  const fetchTasks = useCallback(async () => {
    try { const { data } = await api.get('/tasks/recurring'); setTasks(data); } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) fetchTasks(); }, [isOpen, fetchTasks]);

  const handleToggle = async (taskId, currentValue) => {
    try {
      await api.put(`/tasks/${taskId}`, { is_recurring: !currentValue });
      toast({ title: !currentValue ? 'Recurring enabled' : 'Recurring stopped', status: 'success', duration: 1500 });
      await fetchTasks();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            Recurring Tasks
            <Badge colorScheme="purple" fontSize="11px">{tasks.length} tasks</Badge>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {tasks.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No recurring tasks configured.</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {tasks.map(t => (
                <Flex key={t.id} p={3} border="1px" borderColor={t.is_recurring ? 'purple.200' : 'gray.100'}
                  borderRadius="md" align="center" justify="space-between"
                  bg={t.project_done ? 'gray.50' : 'white'} opacity={t.project_done ? 0.6 : 1}>
                  <Box flex={1}>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontSize="12px" fontWeight="600">{t.task_title || 'Untitled'}</Text>
                      <Badge fontSize="7px" colorScheme="purple">{TYPE_LABELS[t.recurrence_type] || t.recurrence_type}</Badge>
                      {t.recurrence_type === 'days' && t.recurrence_interval && (
                        <Badge fontSize="7px" colorScheme="gray">every {t.recurrence_interval} days</Badge>
                      )}
                      {t.recurrence_type === 'weekly' && t.recurrence_day !== null && (
                        <Badge fontSize="7px" colorScheme="gray">{DAYS[t.recurrence_day]}</Badge>
                      )}
                    </HStack>
                    <Text fontSize="10px" color="gray.500">
                      {t.project_name} {t.company_name && `· ${t.company_name}`}
                    </Text>
                    <HStack fontSize="9px" color="gray.400" spacing={2} mt={0.5}>
                      <Text>Next: {fmtDate(t.next_recurrence_date)}</Text>
                      {t.project_done && <Badge fontSize="7px" colorScheme="red">Project Done</Badge>}
                    </HStack>
                  </Box>
                  <Switch size="md" colorScheme="purple" isChecked={!!t.is_recurring}
                    onChange={() => handleToggle(t.id, t.is_recurring)} />
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
